import * as React from 'react';
import moment from 'moment';
import AlarmIcon from '@material-ui/icons/Alarm';
import AssignmentIcon from '@material-ui/icons/Assignment';
import ContactPhoneIcon from '@material-ui/icons/ContactPhone';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { LoggedUser, PermissionEnum, TaskItemData, TaskItemStatus, TaskItemType, UserData } from '../../models';
import { TK } from '../../store/translations/translationKeys';

export interface TaskDismissReasonOption {
    value: string;
    labelKey: TK;
}

export const TaskDismissReasons = {
    notApplicable: 'Not applicable',
    clientCancelled: 'Client cancelled',
    other: 'Other',
} as const;

export const taskTypesForCreation: TaskItemType[] = Object.values(TaskItemType)
    .filter((value): value is TaskItemType => typeof value === 'number')
    .sort((a, b) => a - b);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getTaskTypeIcon = (taskType?: TaskItemType): React.ComponentType<any> => {
    switch (taskType) {
        case TaskItemType.ReminderClientFollowUp:
            return ContactPhoneIcon;
        case TaskItemType.Reminders:
            return AlarmIcon;
        case TaskItemType.Other:
            return HelpOutlineIcon;
        default:
            return AssignmentIcon;
    }
};

export const canManageTasks = (user?: LoggedUser): boolean => {
    if (!user) return false;
    return user.permissions.includes(PermissionEnum.Administrator)
        || user.permissions.includes(PermissionEnum.AppointmentsManagement)
        || user.permissions.includes(PermissionEnum.ProfessionalsManagement);
};

export const canCreateTasks = (user?: LoggedUser): boolean => {
    return Boolean(user && !user.isClient);
};

export const hasExtendedTaskManagementScope = (user?: LoggedUser, assignableUsers: UserData[] = []): boolean => {
    if (!user) return false;
    return canManageTasks(user) || assignableUsers.some(scopeUser => scopeUser.id !== user.id);
};

export const hasVisibleTaskManagementScope = (tasks: TaskItemData[], user?: LoggedUser): boolean => {
    if (!user) return false;
    return canManageTasks(user) || tasks.some(task => task.assignedUserId !== user.id);
};

export const canUpdateTask = (task: TaskItemData, user?: LoggedUser, canManageVisibleTasks = canManageTasks(user)): boolean => {
    if (!user) return false;
    return canManageVisibleTasks || task.assignedUserId === user.id;
};

export const isTaskOverdue = (task: TaskItemData): boolean => {
    return task.status === TaskItemStatus.Pending && moment(task.dueDate).isBefore(moment());
};

export const getTaskStatusTranslationKey = (status: TaskItemStatus): TK => {
    switch (status) {
        case TaskItemStatus.Completed:
            return TK.taskStatusCompleted;
        case TaskItemStatus.Dismissed:
            return TK.taskStatusDismissed;
        case TaskItemStatus.Pending:
        default:
            return TK.taskStatusPending;
    }
};

export const getTaskTypeTranslationKey = (taskType?: TaskItemType): TK => {
    switch (taskType) {
        case TaskItemType.ReminderClientFollowUp:
            return TK.taskTypeReminderClientFollowUp;
        case TaskItemType.Reminders:
            return TK.taskTypeReminders;
        case TaskItemType.Other:
            return TK.taskTypeOther;
        default:
            return TK.task;
    }
};

export const getTaskDismissReasonOptions = (): TaskDismissReasonOption[] => {
    return [
        { value: TaskDismissReasons.notApplicable, labelKey: TK.dismissReasonNotApplicable },
        { value: TaskDismissReasons.clientCancelled, labelKey: TK.dismissReasonClientCancelled },
        { value: TaskDismissReasons.other, labelKey: TK.other },
    ];
};

export const sortTasksByDueDate = (items: TaskItemData[]): TaskItemData[] => {
    return [...items].sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf());
};

export const sortTasksByStatusThenDueDate = (items: TaskItemData[]): TaskItemData[] => {
    return [...items].sort((a, b) => {
        if (a.status !== b.status) {
            return a.status - b.status;
        }

        return moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf();
    });
};

export const formatTaskDateTime = (value?: string | Date | null): string => {
    if (!value) return '-';

    const parsed = typeof value === 'string'
        ? moment(value, moment.ISO_8601, true)
        : moment(value);

    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '-';
};
